import { useEffect, useRef, useState, useMemo } from 'react';
import { useGameState } from '../hooks/useGameState';
import { Grid } from './Grid';
import { PieceSelector } from './PieceSelector';
import { CurrencyDisplay } from './CurrencyDisplay';
import { TilingLoader } from './TilingLoader';
import { PieceSidebar } from './PieceSidebar';
import { PIECE_DEFINITIONS, getShapeForPiece, getPieceCells, DEFAULT_CONFIG, KEYBINDINGS, type PieceType } from '@game/engine';
import styles from './Game.module.css';

export function Game() {
  const gameState = useGameState();
  const gridRef = useRef<HTMLDivElement>(null);
  const [hoveredCell, setHoveredCell] = useState<{ x: number; y: number } | null>(null);
  const [showTilingLoader, setShowTilingLoader] = useState(false);
  const [pinnedPieceId, setPinnedPieceId] = useState<string | null>(null);
  const [hoveredSelectorType, setHoveredSelectorType] = useState<PieceType | null>(null);
  const [shrinkHover, setShrinkHover] = useState<'width' | 'height' | null>(null);
  const [showPrestigeConfirm, setShowPrestigeConfirm] = useState(false);

  // Create preview piece at hovered cell
  const previewPiece = useMemo(() => {
    if (!hoveredCell || !gameState.selectedPieceType) return undefined;

    const selectedType = gameState.selectedPieceType;
    const piece = {
      type: selectedType,
      rotations: PIECE_DEFINITIONS[selectedType as PieceType],
      currentRotation: gameState.selectedRotation,
      mirrored: gameState.selectedMirrored,
    };

    const shape = getShapeForPiece(piece);
    const anchor = shape.cells[0];

    const preview = {
      ...piece,
      gridX: hoveredCell.x - anchor.x,
      gridY: hoveredCell.y - anchor.y,
    };

    return preview;
  }, [hoveredCell, gameState.selectedPieceType, gameState.selectedRotation, gameState.selectedMirrored]);

  const handleCellClick = (x: number, y: number) => {
    const success = gameState.placePiece(x, y);
    if (success) gameState.selectPieceType(null);
  };

  const handlePieceClick = (pieceId: string) => {
    if (gameState.selectedPieceType) return; // Don't modify pieces while placing
    // Toggle pin
    setPinnedPieceId(prev => prev === pieceId ? null : pieceId);
  };

  const handlePieceContextMenu = (pieceId: string) => {
    if (gameState.selectedPieceType) return; // Don't remove pieces while placing
    gameState.removePiece(pieceId);
    // Clear pin if the pinned piece was removed
    setPinnedPieceId(prev => prev === pieceId ? null : prev);
  };


  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const grid = gridRef.current?.querySelector('[style*="width"][style*="height"]');
    if (!grid) return;

    const rect = grid.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const cellSize = 40;
    const gridX = Math.floor(x / cellSize);
    const gridY = Math.floor(y / cellSize);

    if (gridX >= 0 && gridX < gameState.grid.width && gridY >= 0 && gridY < gameState.grid.height) {
      setHoveredCell({ x: gridX, y: gridY });
    } else {
      setHoveredCell(null);
    }
  };

  const handleMouseLeave = () => {
    setHoveredCell(null);
  };

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const moveKeys = [KEYBINDINGS.moveUp, KEYBINDINGS.moveDown, KEYBINDINGS.moveLeft, KEYBINDINGS.moveRight];
      if (moveKeys.includes(key) || e.key === KEYBINDINGS.place) {
        e.preventDefault(); // prevent page scroll
      }

      if (key === KEYBINDINGS.rotateCW) {
        gameState.rotatePiece(true);
      } else if (key === KEYBINDINGS.rotateCCW) {
        gameState.rotatePiece(false);
      } else if (key === KEYBINDINGS.mirror) {
        gameState.mirrorPiece();
      } else if (e.key === 'Escape') {
        gameState.selectPieceType(null);
        setPinnedPieceId(null);
      } else if (moveKeys.includes(key) && gameState.selectedPieceType) {
        setHoveredCell(prev => {
          const current = prev ?? { x: 0, y: 0 };
          const { width, height } = gameState.grid;
          if (key === KEYBINDINGS.moveUp)    return { ...current, y: Math.max(0, current.y - 1) };
          if (key === KEYBINDINGS.moveDown)  return { ...current, y: Math.min(height - 1, current.y + 1) };
          if (key === KEYBINDINGS.moveLeft)  return { ...current, x: Math.max(0, current.x - 1) };
          if (key === KEYBINDINGS.moveRight) return { ...current, x: Math.min(width - 1, current.x + 1) };
          return current;
        });
      } else if (e.key === KEYBINDINGS.place && hoveredCell) {
        const success = gameState.placePiece(hoveredCell.x, hoveredCell.y);
        if (success) gameState.selectPieceType(null);
      } else {
        const types: PieceType[] = ['I', 'L', 'O', 'S', 'T'];
        const slotIndex = KEYBINDINGS.selectPiece.indexOf(e.key);
        if (slotIndex !== -1) {
          const type = types[slotIndex];
          if (gameState.currency >= DEFAULT_CONFIG.tileConfigs[type].cost) {
            const next = gameState.selectedPieceType === type ? null : type;
            gameState.selectPieceType(next);
            if (next) setPinnedPieceId(null);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameState, hoveredCell]);

  const shrinkPreview = useMemo(() => {
    if (!shrinkHover) return null;
    const { grid, pieces } = gameState;
    const newWidth = shrinkHover === 'width' ? grid.width - 2 : grid.width;
    const newHeight = shrinkHover === 'height' ? grid.height - 2 : grid.height;
    const affectedPieces = pieces
      .filter((p) => getPieceCells(p).some((c) => shrinkHover === 'width' ? c.x >= newWidth : c.y >= newHeight))
      .map((p) => ({ piece: p, refund: Math.floor(DEFAULT_CONFIG.tileConfigs[p.type].cost * DEFAULT_CONFIG.sellRefundRatio) }));
    const pieceRefund = affectedPieces.reduce((sum, { refund }) => sum + refund, 0);
    const currentSizeCfg = DEFAULT_CONFIG.mapSizeConfigs[`${grid.width}x${grid.height}x${gameState.prestigeLevel}`] ?? { tax: 0, rent: 0, expansionCost: 0 };
    const expansionRefund = Math.floor(currentSizeCfg.expansionCost * DEFAULT_CONFIG.sellRefundRatio);
    return { dimension: shrinkHover, affectedPieces, pieceRefund, expansionRefund, totalRefund: pieceRefund + expansionRefund };
  }, [shrinkHover, gameState]);

  const activePiece = pinnedPieceId
    ? gameState.pieces.find(p => p.id === pinnedPieceId) ?? null
    : null;

  return (
    <div className={styles.gameContainer}>
      {showPrestigeConfirm && (
        <div className={styles.modalOverlay} onClick={() => setShowPrestigeConfirm(false)}>
          <div className={styles.modalBox} onClick={e => e.stopPropagation()}>
            <p className={styles.prestigeModalTitle}>Are you sure you want to prestige?</p>
            <p className={styles.prestigeModalBody}>
              Your map will reset to 4×4 and all pieces and expansions will be sold.
              In return, the maximum piece level increases by 1 and expansions become more expensive.
            </p>
            <div className={styles.prestigeModalActions}>
              <button
                className={styles.prestigeConfirmBtn}
                onClick={() => { gameState.prestige(); setShowPrestigeConfirm(false); }}
              >
                Prestige
              </button>
              <button
                className={styles.prestigeCancelBtn}
                onClick={() => setShowPrestigeConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.mainContent}>
          <div className={styles.sideColumn}>
            <CurrencyDisplay currency={gameState.currency} maxCurrency={gameState.maxCurrency} pieces={gameState.pieces} tax={gameState.tax} rent={gameState.rent} />
            <div className={styles.sidePanel}>
              <PieceSidebar
                piece={activePiece}
                allPieces={gameState.pieces}
                isPinned={pinnedPieceId !== null}
                onDelete={() => pinnedPieceId && handlePieceContextMenu(pinnedPieceId)}
                onUpgrade={(pieceId) => gameState.upgradePiece(pieceId)}
                gameEngine={gameState.engine}
                tax={gameState.tax}
                currency={gameState.currency}
                prestigeLevel={gameState.prestigeLevel}
                hoveredSelectorType={pinnedPieceId ? null : (hoveredSelectorType ?? gameState.selectedPieceType)}
                shrinkPreview={shrinkPreview}
              />
            </div>
            {(() => {
              const isAtMaxSize = gameState.grid.width >= 8 && gameState.grid.height >= 8;
              const prestigeCost = DEFAULT_CONFIG.mapSizeConfigs[`8x8x${gameState.prestigeLevel}`]?.prestigeCost ?? 0;
              const canAfford = gameState.currency >= prestigeCost;
              return (
                <>
                  <button
                    className={styles.prestigeButton}
                    disabled={!isAtMaxSize || !canAfford}
                    onClick={() => setShowPrestigeConfirm(true)}
                  >
                    Prestige (${prestigeCost})
                  </button>
                </>
              );
            })()}
          </div>

          <div className={styles.gridSection} ref={gridRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            <PieceSelector
              selectedType={gameState.selectedPieceType}
              currency={gameState.currency}
              onSelectType={(type) => { gameState.selectPieceType(type); if (type) setPinnedPieceId(null); }}
              onHoverType={setHoveredSelectorType}
            />

            <Grid
              grid={gameState.grid}
              pieces={gameState.pieces}
              previewPiece={previewPiece}
              onCellClick={handleCellClick}
              onPieceClick={handlePieceClick}
              onExpandMap={gameState.expandMapSize}
              onShrinkMap={gameState.shrinkMapSize}
              shrinkHover={shrinkHover}
              onShrinkHoverChange={setShrinkHover}
              isPlacementMode={!!gameState.selectedPieceType}
              onPieceContextMenu={handlePieceContextMenu}
              onExitPlacementMode={() => gameState.selectPieceType(null)}
              tax={gameState.tax}
              rent={gameState.rent}
              prestigeLevel={gameState.prestigeLevel}
              selectedPieceId={pinnedPieceId}
            />
          </div>
        </div>

        <p className={styles.hint}>Click/Space to place • WASD to move cursor • Q/E to rotate • R to mirror • 1-5 to select piece • ESC to cancel</p>

        <button
          onClick={() => setShowTilingLoader(!showTilingLoader)}
          style={{
            padding: '8px 12px',
            marginTop: '20px',
            backgroundColor: '#0066cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          Import/Export {showTilingLoader ? '▼' : '▶'}
        </button>

        {showTilingLoader && (
          <TilingLoader
            onLoadTiling={gameState.loadTiling}
            onExportTiling={gameState.exportTiling}
          />
        )}
      </div>
    </div>
  );
}
